import { AutosaveOptions } from '@/components/forms/auto-save-form.js';
import { deepClone, deepEqual } from '@/lib/object-util.js';
import { shortTermStorage } from '@/lib/short-term-storage.js';
import { useRef, useEffect } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';

export interface UseDebouncedFormSubmitOptions<TFieldValues extends FieldValues> extends AutosaveOptions<TFieldValues> {
  form: UseFormReturn<TFieldValues>;
}

export function useDebouncedFormSubmit<TFieldValues extends FieldValues>(
  props: UseDebouncedFormSubmitOptions<TFieldValues>,
): void {
  const { form, delay, longTermPersistent, shortTermPersistent, isSubmitting } = props;

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastValuesRef = useRef<TFieldValues>(form.getValues());

  useEffect(() => {
    if ((!shortTermPersistent && !longTermPersistent) || isSubmitting) return;

    const subscription = form.watch(() => {
      const currentValues = form.getValues() as TFieldValues;

      if (deepEqual(currentValues, lastValuesRef.current)) return;

      lastValuesRef.current = deepClone(currentValues);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        if (shortTermPersistent) {
          const { formKey, validator, onSubmit } = shortTermPersistent;
          const storage = shortTermStorage(formKey, validator);
          storage.set(currentValues);
          onSubmit?.(currentValues);
        }
        if (longTermPersistent) {
          const { validator, onSubmit } = longTermPersistent;

          if (!validator) {
            onSubmit(currentValues);
            return;
          }

          const parsedData = validator ? validator.safeParse(currentValues) : currentValues;

          if (!parsedData.success) return;
          onSubmit(parsedData.data);
        }
      }, delay.total('milliseconds'));
    });

    return () => {
      subscription.unsubscribe();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, isSubmitting]);
}
